'use client';

import { InfiniteData, useInfiniteQuery, UseInfiniteQueryResult } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useIntersectionObserver } from 'usehooks-ts';

import { BasicType, Pageable } from '@/service/commonType';

interface ResponseData<T> {
  items: T[];
  pageable: Pageable;
}

interface DataType<T> extends BasicType {
  data: ResponseData<T>;
}

interface ParameterType<T> {
  queryFn: any;
  setDataLength?: (number: number) => void;
  isSuspense?: boolean;
}

interface ResultType<T> {
  dataArray: T[] | undefined;
  ref: (node?: Element | null) => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  refetch: () => void;
  isLoading: boolean;
}

export default function useInfinityBigHook<T>({
  queryFn,
  setDataLength,
  isSuspense = false,
}: ParameterType<T>): ResultType<T> {
  const { isIntersecting, ref } = useIntersectionObserver({
    threshold: 0,
    rootMargin: '10px 0px',
  });

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
    isLoading,
  }: UseInfiniteQueryResult<InfiniteData<DataType<T>>> = useInfiniteQuery({
    ...queryFn,
    ...basicInfiniteQueryOptions<T>(queryFn.queryKey[1]?.limit ?? 10),
    suspense: isSuspense,
  });

  useEffect(() => {
    if (isIntersecting && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [isIntersecting, fetchNextPage, hasNextPage, isFetchingNextPage]);

  useEffect(() => {
    if (setDataLength && data?.pages[0]?.data?.pageable?.totalCount !== undefined) {
      setDataLength(data.pages[0].data.pageable.totalCount);
    }
  }, [data?.pages[0]?.data?.pageable?.totalCount]);

  const dataArray = useMemo(() => data?.pages.flatMap((page) => page.data.items), [data?.pages]);

  return { dataArray, ref, hasNextPage, isFetchingNextPage, refetch, isLoading };
}

export const basicInfiniteQueryOptions = <T>(limit: number) => ({
  initialPageParam: 1,
  getNextPageParam: (lastPage: DataType<T>, pages: DataType<T>[]) => {
    if (lastPage.data.items.length < limit) return undefined;
    return pages.length + 1;
  },
});
